import { useEffect, useState, useCallback, useRef } from "react";
import { ops, apiError, fmtDate, fmtDT, todayISO } from "./opsApi";
import { inputCls, Field, Btn, Badge, Card, CardTitle, TabBar, Empty, Msg } from "./opsUi";

const useMsg = () => {
  const [msg, setMsg] = useState(null);
  const timer = useRef(null);
  const flash = (ok, text) => {
    if (timer.current) clearTimeout(timer.current);
    setMsg({ ok, text });
    timer.current = setTimeout(() => setMsg(null), 5000);
  };
  return [msg, flash];
};

const EvalForm = ({ session, flash, onDone }) => {
  const [f, setF] = useState({ otp: "", confidence: "same", panic: "none", repeated_mistakes: "", road_awareness: "average", remarks: "", next_focus: "" });
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await ops.post(`/sessions/${session.id}/end`, { otp: f.otp, evaluation: { ...f, otp: undefined } });
      flash(true, "Session completed & evaluation saved");
      onDone();
    } catch (err) { flash(false, apiError(err)); }
  };
  return (
    <form onSubmit={submit} className="mt-3 space-y-3 rounded-xl bg-dwc-soft/70 p-4" data-testid={`eval-form-${session.id}`}>
      <p className="text-[11px] font-extrabold uppercase text-dwc-charcoal/60">End Session — Mandatory Evaluation</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="END OTP from student">
          <input value={f.otp} onChange={set("otp")} className={inputCls} required minLength={4} inputMode="numeric" data-testid={`end-otp-input-${session.id}`} />
        </Field>
        <Field label="1. Confidence">
          <select value={f.confidence} onChange={set("confidence")} className={inputCls} data-testid={`eval-confidence-${session.id}`}>
            <option value="worse">Worse</option><option value="same">Same</option><option value="better">Better</option>
          </select>
        </Field>
        <Field label="2. Panic / anxiety">
          <select value={f.panic} onChange={set("panic")} className={inputCls} data-testid={`eval-panic-${session.id}`}>
            <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option><option value="none">None</option>
          </select>
        </Field>
        <Field label="4. Road awareness">
          <select value={f.road_awareness} onChange={set("road_awareness")} className={inputCls} data-testid={`eval-awareness-${session.id}`}>
            <option value="slow">Slow</option><option value="average">Average</option><option value="quick_calm">Quick & Calm</option>
          </select>
        </Field>
        <Field label="3. Repeated mistakes">
          <input value={f.repeated_mistakes} onChange={set("repeated_mistakes")} className={inputCls} placeholder="e.g. late braking" data-testid={`eval-mistakes-${session.id}`} />
        </Field>
        <Field label="5. Next-session focus">
          <input value={f.next_focus} onChange={set("next_focus")} className={inputCls} placeholder="e.g. parallel parking" data-testid={`eval-focus-${session.id}`} />
        </Field>
      </div>
      <Field label="Trainer remarks">
        <textarea value={f.remarks} onChange={set("remarks")} rows={2} className={inputCls} data-testid={`eval-remarks-${session.id}`} />
      </Field>
      <Btn type="submit" testId={`end-session-btn-${session.id}`}>Verify OTP & Complete Session</Btn>
    </form>
  );
};

const SessionCard = ({ s, flash, reload }) => {
  const [otpField, setOtpField] = useState("");
  const [requesting, setRequesting] = useState(false);
  const requestOtp = async (purpose) => {
    setRequesting(true);
    try {
      const r = await ops.post(`/sessions/${s.id}/otp?purpose=${purpose}`);
      flash(true, r.message);
    } catch (err) { flash(false, apiError(err)); }
    setRequesting(false);
  };
  const start = async () => {
    try {
      await ops.post(`/sessions/${s.id}/start`, { otp: otpField });
      flash(true, "Session started");
      reload();
    } catch (err) { flash(false, apiError(err)); }
  };
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4" data-testid={`trainer-session-${s.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-black text-dwc-charcoal">{fmtDate(s.date)} · {s.time} <Badge value={s.status} /></p>
          <p className="text-[11px] font-bold text-dwc-charcoal/55">{s.student_name} · {s.student_mobile} · {s.duration_min} min {s.note && `· ${s.note}`}</p>
        </div>
      </div>
      {s.status === "scheduled" && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Btn small variant="outline" onClick={() => requestOtp("start")} disabled={requesting} testId={`request-start-otp-${s.id}`}>Send START OTP</Btn>
          <input value={otpField} onChange={(e) => setOtpField(e.target.value)} placeholder="Enter OTP" inputMode="numeric"
            className="w-28 rounded-full border border-gray-200 px-3 py-1.5 text-sm font-bold outline-none focus:border-dwc-green" data-testid={`start-otp-input-${s.id}`} />
          <Btn small onClick={start} disabled={otpField.length < 4} testId={`start-session-btn-${s.id}`}>Start Session</Btn>
        </div>
      )}
      {s.status === "in_progress" && (
        <>
          <div className="mt-3">
            <Btn small variant="outline" onClick={() => requestOtp("end")} disabled={requesting} testId={`request-end-otp-${s.id}`}>Send END OTP</Btn>
          </div>
          <EvalForm session={s} flash={flash} onDone={reload} />
        </>
      )}
    </div>
  );
};

const MySchedule = ({ flash }) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => ops.get("/trainer/overview").then(setData).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Empty>Loading schedule...</Empty>;
  const today = data.sessions.filter((s) => s.date === data.today);
  const upcoming = data.sessions.filter((s) => s.date > data.today);
  return (
    <div className="space-y-5">
      <Card testId="trainer-today-card">
        <CardTitle>Today — {fmtDate(data.today)}</CardTitle>
        {today.length === 0 && <Empty>No sessions today.</Empty>}
        <div className="space-y-3">{today.map((s) => <SessionCard key={s.id} s={s} flash={flash} reload={load} />)}</div>
      </Card>
      <Card testId="trainer-upcoming-card">
        <CardTitle>Upcoming</CardTitle>
        {upcoming.length === 0 && <Empty>No upcoming sessions.</Empty>}
        <div className="space-y-2">
          {upcoming.map((s) => (
            <p key={s.id} className="text-sm font-bold text-dwc-charcoal">{fmtDate(s.date)} · {s.time} — {s.student_name} <Badge value={s.status} /></p>
          ))}
        </div>
      </Card>
    </div>
  );
};

const MyStudents = () => {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => { ops.get("/trainer/overview").then(setData).catch(() => {}); }, []);
  const openDetail = (id) => ops.get(`/trainer/students/${id}`).then(setDetail).catch(() => {});
  if (!data) return <Empty>Loading students...</Empty>;
  return (
    <div className="space-y-5">
      <Card testId="trainer-students-card">
        <CardTitle>My Assigned Students</CardTitle>
        {data.students.length === 0 && <Empty>No students assigned yet.</Empty>}
        <div className="space-y-2.5">
          {data.students.map((st) => (
            <div key={st.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-100 p-3" data-testid={`trainer-student-${st.mobile}`}>
              <div>
                <p className="text-sm font-black text-dwc-charcoal">{st.name}</p>
                <p className="text-[11px] font-bold text-dwc-charcoal/55">
                  {st.mobile} · {st.completed_sessions}/{st.package_total || "—"} sessions
                  {st.last_evaluation?.evaluation?.next_focus && ` · Next focus: ${st.last_evaluation.evaluation.next_focus}`}
                </p>
              </div>
              <Btn small variant="outline" onClick={() => openDetail(st.id)} testId={`view-student-${st.mobile}`}>History & Notes</Btn>
            </div>
          ))}
        </div>
      </Card>
      {detail && (
        <Card testId="student-detail-card">
          <div className="flex items-center justify-between">
            <CardTitle>{detail.student.name} — Training History</CardTitle>
            <Btn small variant="outline" onClick={() => setDetail(null)}>Close</Btn>
          </div>
          {detail.sessions.length === 0 && <Empty>No sessions yet for this student.</Empty>}
          <div className="space-y-2.5">
            {detail.sessions.map((s) => (
              <div key={s.id} className="rounded-xl bg-dwc-soft/60 p-3">
                <p className="text-sm font-bold text-dwc-charcoal">{fmtDate(s.date)} · {s.time} <Badge value={s.status} /> <span className="text-[11px] text-dwc-charcoal/50">by {s.trainer_name}</span></p>
                {s.evaluation && (
                  <p className="mt-1 text-[11px] font-semibold text-dwc-charcoal/70">
                    Confidence: <b>{s.evaluation.confidence}</b> · Panic: <b>{s.evaluation.panic}</b> · Awareness: <b>{String(s.evaluation.road_awareness).replace("_", " & ")}</b>
                    {s.evaluation.remarks && <> · {s.evaluation.remarks}</>}
                    {s.evaluation.next_focus && <> · Next: {s.evaluation.next_focus}</>}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

const ReportIssue = ({ flash }) => {
  const [f, setF] = useState({ type: "delay", description: "" });
  const [leave, setLeave] = useState({ date_from: todayISO(), date_to: todayISO(), reason: "" });
  const [mine, setMine] = useState([]);
  const [tasks, setTasks] = useState([]);
  const load = useCallback(() => {
    ops.get("/incidents").then(setMine).catch(() => {});
    ops.get("/tasks").then(setTasks).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => {
    e.preventDefault();
    try { await ops.post("/incidents", f); flash(true, "Report submitted to DWC management"); setF({ type: "delay", description: "" }); load(); }
    catch (err) { flash(false, apiError(err)); }
  };
  const applyLeave = async (e) => {
    e.preventDefault();
    try { await ops.post("/leaves", leave); flash(true, "Leave request submitted"); setLeave({ date_from: todayISO(), date_to: todayISO(), reason: "" }); }
    catch (err) { flash(false, apiError(err)); }
  };
  const doneTask = async (t) => {
    try { await ops.put(`/tasks/${t.id}`, { status: "done" }); load(); } catch (err) { flash(false, apiError(err)); }
  };
  return (
    <div className="space-y-5">
      <Card testId="trainer-report-card">
        <CardTitle>Report Delay / Incident</CardTitle>
        <form onSubmit={submit} className="space-y-3" data-testid="trainer-incident-form">
          <Field label="Type">
            <select value={f.type} onChange={(e) => setF((v) => ({ ...v, type: e.target.value }))} className={inputCls} data-testid="incident-type-select">
              <option value="delay">Delay</option>
              <option value="student_unavailable">Student unavailable</option>
              <option value="vehicle_damage">Vehicle damage</option>
              <option value="safety">Safety incident</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Description">
            <textarea value={f.description} onChange={(e) => setF((v) => ({ ...v, description: e.target.value }))} rows={3} className={inputCls} required minLength={5} data-testid="incident-desc-input" />
          </Field>
          <Btn type="submit" testId="incident-submit-btn">Submit Report</Btn>
        </form>
      </Card>
      <Card testId="trainer-leave-card">
        <CardTitle>Apply for Leave</CardTitle>
        <form onSubmit={applyLeave} className="grid gap-3 sm:grid-cols-4" data-testid="trainer-leave-form">
          <Field label="From"><input type="date" value={leave.date_from} onChange={(e) => setLeave((v) => ({ ...v, date_from: e.target.value }))} className={inputCls} required /></Field>
          <Field label="To"><input type="date" value={leave.date_to} onChange={(e) => setLeave((v) => ({ ...v, date_to: e.target.value }))} className={inputCls} required /></Field>
          <Field label="Reason"><input value={leave.reason} onChange={(e) => setLeave((v) => ({ ...v, reason: e.target.value }))} className={inputCls} required minLength={3} data-testid="trainer-leave-reason" /></Field>
          <div className="flex items-end"><Btn type="submit" testId="trainer-leave-btn">Apply</Btn></div>
        </form>
      </Card>
      <Card testId="trainer-tasks-card">
        <CardTitle>My Tasks</CardTitle>
        {tasks.length === 0 && <Empty>No tasks assigned.</Empty>}
        <div className="space-y-2">
          {tasks.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-100 p-3">
              <p className={`text-sm font-bold ${t.status === "done" ? "text-dwc-charcoal/40 line-through" : "text-dwc-charcoal"}`}>{t.title} {t.due_date && <span className="text-[11px] text-dwc-charcoal/50">due {fmtDate(t.due_date)}</span>}</p>
              {t.status !== "done" && <Btn small variant="outline" onClick={() => doneTask(t)} testId={`trainer-done-task-${t.id}`}>Done</Btn>}
            </div>
          ))}
        </div>
      </Card>
      <Card testId="trainer-my-reports-card">
        <CardTitle>My Submitted Reports</CardTitle>
        {mine.length === 0 && <Empty>Nothing reported yet.</Empty>}
        <div className="space-y-2">
          {mine.map((i) => (
            <p key={i.id} className="text-xs font-semibold text-dwc-charcoal/70">
              [{i.type.replace(/_/g, " ")}] {i.description} <Badge value={i.status} /> <span className="text-dwc-charcoal/40">{fmtDT(i.created_at)}</span>
              {i.resolution && <span className="text-emerald-700"> — {i.resolution}</span>}
            </p>
          ))}
        </div>
      </Card>
    </div>
  );
};

const Resources = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { ops.get("/trainer/resources").then(setItems).catch(() => {}); }, []);
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {items.map((r) => (
        <Card key={r.id} testId={`resource-${r.id}`}>
          <CardTitle>{r.title}</CardTitle>
          <ul className="space-y-1.5">
            {r.items.map((it, i) => (
              <li key={i} className="flex gap-2 text-sm font-semibold text-dwc-charcoal/75">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-dwc-green" />{it}
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
};

export default function TrainerDashboard() {
  const [tab, setTab] = useState("schedule");
  const [msg, flash] = useMsg();
  return (
    <div className="space-y-5" data-testid="trainer-dashboard">
      <TabBar prefix="trainer" tab={tab} setTab={setTab} tabs={[
        ["schedule", "My Schedule"], ["students", "My Students"], ["report", "Report & Tasks"], ["resources", "Playbook & SOPs"],
      ]} />
      <Msg msg={msg} />
      {tab === "schedule" && <MySchedule flash={flash} />}
      {tab === "students" && <MyStudents />}
      {tab === "report" && <ReportIssue flash={flash} />}
      {tab === "resources" && <Resources />}
    </div>
  );
}
